// Theme: System / Light / Dark (§11b). The pre-paint script in index.html applies the stored
// choice before first paint; this module keeps it in sync afterwards.
import { useSyncExternalStore } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';
export const THEME_KEY = 'wd-theme';

const media = () =>
  typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

function read(): ThemeMode {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

let current: ThemeMode = typeof window === 'undefined' ? 'system' : read();
const listeners = new Set<() => void>();

export function resolveDark(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && !!media()?.matches);
}

function apply() {
  const dark = resolveDark(current);
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? '#0e1014' : '#ffffff');
}

/** Sets the theme locally (localStorage). Callers that are signed in also PATCH /api/me. */
export function setTheme(mode: ThemeMode): void {
  current = mode;
  try {
    localStorage.setItem(THEME_KEY, mode);
  } catch {
    /* private mode: still applies for this page */
  }
  apply();
  listeners.forEach((l) => l());
}

/** Re-write the stored key after Clear-Site-Data wiped storage on logout (§11c). */
export function persistTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function getTheme(): ThemeMode {
  return current;
}

if (typeof window !== 'undefined') {
  media()?.addEventListener('change', () => {
    if (current === 'system') {
      apply();
      listeners.forEach((l) => l());
    }
  });
  // Another tab changed the theme.
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_KEY) {
      current = read();
      apply();
      listeners.forEach((l) => l());
    }
  });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useTheme(): { mode: ThemeMode; dark: boolean; setTheme: typeof setTheme } {
  const mode = useSyncExternalStore(subscribe, getTheme, () => 'system' as ThemeMode);
  return { mode, dark: resolveDark(mode), setTheme };
}
